// src/utils/customerValidation.js
/**
 * Shared validation helpers for customer-shaped contact fields.
 *
 * The customer form (`components/customers/CustomerForm.jsx`) and the header's
 * own edit modal (`components/customers/CustomerHeader.jsx`) are two separate,
 * independently-written copies of the same fields. Before this file each one
 * carried its own inline regex literals; the landlord fields added on
 * 2026-09-05 are validated in both places, so the patterns live here once and
 * are imported rather than retyped. Same rules, one source.
 */

/** Customer/landlord email — must look like `a@b.c`. */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Customer/landlord phone — exactly 11 digits (UK mobile/landline as typed). */
export const PHONE_REGEX = /^\d{11}$/;

/** The four optional landlord fields on `customer/{id}`. Added 2026-09-05. */
export const LANDLORD_FIELDS = [
  "landlordName",
  "landlordEmail",
  "landlordPhone",
  "landlordPostCode",
];

const trimmed = (value) => (typeof value === "string" ? value.trim() : "");

export const isValidEmail = (value) => EMAIL_REGEX.test(trimmed(value));

export const isValidPhone = (value) => PHONE_REGEX.test(trimmed(value));

/**
 * Validate the landlord block.
 *
 * Every landlord field is optional: an empty field is always valid. A field
 * that HAS been filled in is held to exactly the same standard as the
 * customer's own equivalent field. Name and post code have no format rule
 * (the customer's own post code has none either).
 *
 * @param {Object} data - object carrying any of the LANDLORD_FIELDS
 * @returns {Object} errors keyed by field name; empty object means valid
 */
export const validateLandlordFields = (data = {}) => {
  const errors = {};

  const email = trimmed(data.landlordEmail);
  if (email && !isValidEmail(email)) {
    errors.landlordEmail = "Please enter a valid email address";
  }

  const phone = trimmed(data.landlordPhone);
  if (phone && !isValidPhone(phone)) {
    errors.landlordPhone = "Phone number must be 11 digits";
  }

  return errors;
};

/**
 * True when a customer record carries at least one non-empty landlord field.
 * Used to decide whether to render the Landlord block at all, so customers
 * with no landlord don't get a row of "Not specified".
 *
 * @param {Object} customer - customer record
 * @returns {boolean}
 */
export const hasLandlordDetails = (customer) =>
  LANDLORD_FIELDS.some((field) => trimmed(customer?.[field]) !== "");
